import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function HeroCalendar() {
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/availability")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.days || []);
        setDays(list);
        const first = list.find((d) => d.available);
        if (first) setSelected(first.date);
      })
      .finally(() => setLoading(false));
  }, []);

  const parse = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  return (
    <div
      data-testid="hero-calendar"
      className="w-full rounded-[1.6rem] border border-brand-silver/50 bg-white/90 p-6 shadow-[0_30px_80px_-40px_rgba(26,28,30,0.45)] backdrop-blur-xl sm:p-7"
    >
      <div className="flex items-center justify-between">
        <p className="overline text-brand-red">Pick your day — we'll handle the rest</p>
        <span className="hidden text-xs text-brand-muted sm:block">Next 3 weeks</span>
      </div>

      {loading ? (
        <div className="flex h-44 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand-red" />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-7 gap-1.5 sm:gap-2">
          {days.map((d) => {
            const date = parse(d.date);
            const isSel = selected === d.date;
            return (
              <button
                key={d.date}
                data-testid={`calendar-day-${d.date}`}
                disabled={!d.available}
                onClick={() => setSelected(d.date)}
                className={[
                  "group flex aspect-square flex-col items-center justify-center rounded-xl border text-center transition-[transform,background-color,border-color,color] duration-200",
                  isSel
                    ? "border-brand-red bg-brand-red text-white shadow-[0_10px_24px_-10px_rgba(200,16,46,0.7)] scale-[1.03]"
                    : d.available
                      ? "border-brand-silver/40 bg-white text-brand-charcoal hover:border-brand-red hover:-translate-y-0.5"
                      : "border-transparent bg-brand-offwhite text-brand-silver/60 cursor-not-allowed line-through",
                ].join(" ")}
              >
                <span className={`text-[0.55rem] font-semibold uppercase tracking-wider ${isSel ? "text-white/80" : "text-brand-muted"}`}>
                  {WEEKDAYS[date.getDay()]}
                </span>
                <span className="text-base font-semibold leading-none sm:text-lg">{date.getDate()}</span>
                <span className={`text-[0.5rem] uppercase ${isSel ? "text-white/70" : "text-brand-muted/70"}`}>
                  {MONTHS[date.getMonth()]}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <button
        data-testid="hero-book-date-button"
        disabled={!selected}
        onClick={() => navigate(`/book?date=${selected}`)}
        className="btn-red mt-6 w-full disabled:opacity-50"
      >
        Book This Date <ArrowRight className="h-4 w-4" />
      </button>
      <p className="mt-3 text-center text-xs text-brand-muted">
        Book in 60 seconds · 10% Summer discount auto-applied
      </p>
    </div>
  );
}
